import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, map, Observable, switchMap, tap } from 'rxjs';
import { Editor } from 'ngx-editor';
import { DateTime } from 'luxon';

import { MessageService } from '../../core/services/message.service';
import { Message } from '../../core/models/message';
import { MessageDocument } from '../../core/models/message-document';
import { QuestionnairePortalApiService } from '../../core/services/questionnaire-portal-api.service';
import { MessageListService } from '../../core/services/message-list.service';
import { MatDialog } from '@angular/material/dialog';
import { ClientDocument } from '../../core/models/client-document';
import { Router, UrlTree, ActivatedRoute } from '@angular/router';
import { OnlineQuestionnaire } from '../../core/models/online-questionnaire';
import { Questionnaire } from '../../core/models/questionnaire';

interface uriResponse {
  uri: string;
}

@Component({
  selector: 'app-view-message',
  templateUrl: './view-message.component.html',
  styleUrl: './view-message.component.scss',
})
export class ViewMessageComponent implements OnDestroy, OnInit {

  message!: Message;
  messageAttachments!: MessageDocument[];
  onlineQuestionnaire: OnlineQuestionnaire | null = null;
  questionnaire : Questionnaire | null = null;

  editor: Editor;

  sortedDocuments: ClientDocument[] | null = null;
  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  @Input() id!: string;
  returnUrl: string | null | undefined;

  constructor(private messageService: MessageService,
     private questionnairePortalApiService: QuestionnairePortalApiService,
      private messageListService: MessageListService,
       private dialog: MatDialog,
         private router: Router,
          private activatedRoute: ActivatedRoute) {
    this.editor = new Editor();

   
  }

  ngOnInit(): void {
    this.returnUrl = this.activatedRoute.snapshot.queryParamMap.get('returnUrl');
    this.loading$.next(true);

   this.messageService.getMessage(this.id).pipe(
      switchMap((message) => {
      return this.messageService.readMessage(message.id).pipe(
        switchMap(() => {
          this.messageListService.refreshUnreadMessages();
          this.messageListService.refreshInboxMessages();
          return this.messageService.getMessageAttachments(message.id).pipe(
            map(attachments => ({ message, attachments }))
          );
        })
      );
      })
    ).subscribe(
      ({ message, attachments }) => {
        this.message = message;
        this.messageAttachments = attachments;

        if (message.onlineQuestionnaireId) {
          this.questionnairePortalApiService.GetQuestionnaire(message.onlineQuestionnaireId).subscribe(q => {
            this.questionnaire = q;
            this.loading$.next(false);
          });
          this.questionnairePortalApiService.GetOnlineQuestionnaire(message.onlineQuestionnaireId).subscribe(oq => {
            this.onlineQuestionnaire = oq;
            this.loading$.next(false);
          });
        }
        else{
          this.loading$.next(false);
        }

      }
    );
  }

  onClose() {
    this.router.navigateByUrl(this.returnUrl ?? '/home');
  }

    // Check if the questionaire is marked as deleted.
  isQuestionnaireDeleted = () => {
    return this.onlineQuestionnaire
      && this.onlineQuestionnaire.status === 'Deleted';
  }

  //Check if the questionnaire is marked as expired.
  isQuestionnaireExpired = () => {
    return this.questionnaire
      && this.questionnaire.expiredDate !== null;
  }




  /**
   * Authenticate the current user against the legacy document portal, and redirect to the questionnaire
   * @param questionnaireId 
   */
  OpenQuestionnaire(questionnaireId: string) {
    window.location.href = `/QuestionnairePortal2/${questionnaireId}`;
  }


  ngOnDestroy(): void {
    this.editor.destroy();
  }

  printMessage() {
    window.print();
  }

  attachmentNotExpired(messageAttachment: MessageDocument) {
    return messageAttachment.expiresDateTime && DateTime.fromISO(messageAttachment.expiresDateTime).diffNow().milliseconds > 0;
  }

  downloadAttachment(messageAttachmentId: string) {
    this.messageService.downloadMessageAttachment(this.message.id, messageAttachmentId).subscribe(response => {
      if (response.body == null) {
        return;
      }

      let fileName = response.headers.get('Content-Disposition');
      if (fileName == null) {
        fileName = messageAttachmentId;
      } else {
        try {
          fileName = fileName.split(';')[1].split('=')[1];
        } catch (error) {
          fileName = messageAttachmentId;          
        }
      }

      const blob = new Blob([response.body], { type: response.headers.get('Content-Type') ?? undefined });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    });
  }

  getFileIcon(messageAttachment: MessageDocument) {
    if (messageAttachment.fileName == null) {
      return 'description';
    }

    const arr = messageAttachment.fileName.split('.');
    const fileType = arr[arr.length - 1];

    if (fileType === 'doc' || fileType === 'docx' || fileType === 'dot' || fileType === 'dotx')
      return 'description';
    if (fileType === 'gif' || fileType === 'jpg' || fileType === 'jpeg' || fileType === 'png')
      return 'image';
    if (fileType === 'xls' || fileType === 'xlsx')
      return 'table_view';
    if (fileType === 'ppt' || fileType === 'pptx')
      return 'slideshow';
    if (fileType === 'pdf')
      return 'picture_as_pdf';
    if (fileType === 'zip')
      return 'folder_zip';
    if (fileType === 'txt' || fileType === 'rtf')
      return 'description';

    return 'description';
  }


  getReplyMessageRouterLink() : UrlTree {

    return this.router.createUrlTree(['/message-compose'], { queryParams: { returnUrl: this.router.url, replyMessageId: this.message.id } });
         
  }


}
